/**
 * CORE-1105 — the idle aura's timing, pulled out of the component so its cost
 * is a number a test can hold rather than a claim in a commit message.
 *
 * `LauncherAura` runs inside a widget mounted at the app root (`App.tsx`), so
 * whatever it animates, it animates on every screen for as long as the tab is
 * open. On react-native-web there is no native animated module, so every one of
 * those animations is driven from JS: a `requestAnimationFrame` loop per
 * `Animated.Value`, re-interpolating and writing style to its node every frame.
 *
 * Three of the five values are sparks, and they were built as
 * `Animated.loop(sequence([delay, flashUp, flashDown, rest]))`. `Animated.delay`
 * is a real animation — it ticks every frame and writes the same unchanged
 * style — so each spark was animating flat out while spending most of its cycle
 * doing visually nothing:
 *
 *     spark @35°    1500 ms alive of a 4300 ms cycle — 65 % idle
 *     spark @158°   1700 ms alive of a 6100 ms cycle — 72 % idle
 *     spark @283°   1300 ms alive of a 5400 ms cycle — 76 % idle
 *
 * The waits are now plain timers, so a spark animates only while it is visibly
 * alive. The rhythm is untouched: the gap between two flashes is still
 * `rest + delay`, exactly as the old loop produced it, which is what keeps the
 * three sparks on different periods and the whole thing looking irregular
 * (CORE-847 §3 asks for «appearing irregularly»).
 *
 * NOT done here, deliberately — both would be no-ops dressed as optimisations:
 *   • pausing on `document.hidden` — `requestAnimationFrame` does not fire in a
 *     hidden tab at all (established in CORE-1102), so there is nothing left to
 *     pause;
 *   • an off-screen/IntersectionObserver check — the launcher is a fixed-position
 *     corner element and is never scrolled out of view.
 */

export interface SparkSpec {
  /** Angle on the ring, degrees. */
  angle: number;
  size: number;
  /** How long the spark is visibly alive — the only stretch that must animate. */
  duration: number;
  /** Wait before the first flash, and again after every rest. */
  delay: number;
  /** Wait after a flash, before the next `delay`. */
  rest: number;
}

/** Angle on the ring, lifetime, and how long until this spark returns. */
export const SPARKS: SparkSpec[] = [
  { angle: 35, size: 4, duration: 1500, delay: 400, rest: 2400 },
  { angle: 158, size: 3, duration: 1700, delay: 1300, rest: 3100 },
  { angle: 283, size: 3.5, duration: 1300, delay: 2100, rest: 2000 },
];

/** Flash to flash. Unchanged from the original loop — the rhythm is the design. */
export const sparkCycleMs = (s: SparkSpec) => s.delay + s.duration + s.rest;

/** The gap the component waits out with a timer instead of an animation. */
export const sparkQuietMs = (s: SparkSpec) => s.delay + s.rest;

/**
 * Share of a cycle a spark actually animates, 0..1. Was 1 for every spark:
 * `Animated.delay` animates too.
 */
export const sparkDutyCycle = (s: SparkSpec) => s.duration / sparkCycleMs(s);

/**
 * How many of the aura's five values are animating at a given moment, averaged
 * over time. The two constant loops (glow breath, ring sweep) are always on;
 * the sparks now contribute only their duty cycle.
 */
export const averageActiveValues = (sparks: SparkSpec[] = SPARKS) =>
  2 + sparks.reduce((sum, s) => sum + sparkDutyCycle(s), 0);
